import { useMemo, useState, type FormEvent } from 'react';
import { useOp } from '../../api.ts';
import { useRouter } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { Empty, ErrorBox, Loading, Modal } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import { TYPE_LABELS, type ApiKey, type TenancyOverview } from './types.ts';
import { ConfirmButton, since } from './ui.tsx';

/** API keys per site, for embeds and partners. A new key is shown once; only its hash is stored. */
export function ApiKeys() {
  const { query, setQuery } = useRouter();
  const overview = useOp<TenancyOverview>('admin.tenancy.overview');
  const sites = useMemo(
    () => overview.data?.organizations.flatMap((o) => o.brands.flatMap((b) => b.sites.filter((s) => s.canAdmin).map((s) => ({ ...s, label: `${o.name} · ${s.name}` })))) ?? [],
    [overview.data],
  );
  const siteRef = query.get('site') ?? sites[0]?.ref ?? null;
  const site = sites.find((s) => s.ref === siteRef);

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>API keys</h1>
          <p className="muted">Keys let a publisher's page or a partner call the public API for one site. Only the sites you administer are listed.</p>
        </div>
      </header>
      {overview.error ? <ErrorBox error={overview.error} onRetry={overview.reload} /> : null}
      {!overview.data ? <Loading /> : null}
      {overview.data && sites.length === 0 ? <Empty>You administer no sites.</Empty> : null}
      {sites.length ? (
        <div className="toolbar">
          <label className="inline-field">
            Site
            <select value={siteRef ?? ''} onChange={(e) => setQuery({ site: e.target.value }, { replace: true })}>
              {sites.map((s) => (
                <option key={s.ref} value={s.ref}>
                  {s.label} ({TYPE_LABELS[s.type] ?? s.type})
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
      {site ? <KeysForSite siteRef={site.ref} siteName={site.name} /> : null}
    </section>
  );
}

function KeysForSite({ siteRef, siteName }: { siteRef: string; siteName: string }) {
  const toast = useToast();
  const changes = useChanges();
  const keys = useOp<{ keys: ApiKey[] }>('admin.apiKeys.list', { siteRef }, changes);
  const [name, setName] = useState('');
  const [origins, setOrigins] = useState('');
  const [secret, setSecret] = useState<{ secret: string; name: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const create = async (event: FormEvent) => {
    event.preventDefault();
    const allowedOrigins = origins.split(/[\s,]+/).map((o) => o.trim()).filter(Boolean);
    const result = await runWrite<{ secret: string }>(toast, 'admin.apiKeys.create', { siteRef, name, allowedOrigins }, 'API key created');
    if (result) {
      setSecret({ secret: result.secret, name });
      setCopied(false);
      setName('');
      setOrigins('');
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret!.secret);
      setCopied(true);
    } catch {
      document.querySelector<HTMLInputElement>('[data-secret]')?.select();
    }
  };

  return (
    <>
      {keys.error ? <ErrorBox error={keys.error} onRetry={keys.reload} /> : null}
      {!keys.data ? <Loading /> : null}
      {keys.data ? (
        <table className="table compact" aria-label={`API keys of ${siteName}`}>
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Key</th>
              <th scope="col">Allowed origins</th>
              <th scope="col">Created</th>
              <th scope="col">Last used</th>
              <th scope="col">Status</th>
              <th scope="col" className="actions">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {keys.data.keys.map((key) => (
              <tr key={key.ref} className={key.active ? undefined : 'muted'}>
                <th scope="row">{key.name}</th>
                <td>
                  <code>{key.prefix ? `${key.prefix}…` : 'older key'}</code>
                </td>
                <td className="small">{key.allowedOrigins.length ? key.allowedOrigins.join(', ') : <span className="muted">any (server to server)</span>}</td>
                <td className="small">
                  {since(key.createdAt)}
                  {key.createdBy ? <small className="block muted">{key.createdBy}</small> : null}
                </td>
                <td className="small">{since(key.lastUsedAt)}</td>
                <td>{key.active ? <span className="badge badge-ok">Active</span> : <span className="badge">Revoked {since(key.revokedAt)}</span>}</td>
                <td className="actions">
                  {key.active ? (
                    <ConfirmButton label="Revoke" title={`Revoke “${key.name}”?`} confirmLabel="Revoke key" onConfirm={() => runWrite(toast, 'admin.apiKeys.revoke', { ref: key.ref }, 'Key revoked')}>
                      <p>Calls with this key stop working at once. This cannot be undone; create a new key instead.</p>
                    </ConfirmButton>
                  ) : null}
                </td>
              </tr>
            ))}
            {keys.data.keys.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted">
                  No keys yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      ) : null}
      <form className="inline-form" onSubmit={create} aria-label="Create API key">
        <label>
          Key name
          <input required minLength={2} value={name} placeholder="Daily embed (production)" onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="grow">
          Allowed origins
          <input value={origins} placeholder="https://www.publisher.com" onChange={(e) => setOrigins(e.target.value)} />
        </label>
        <button type="submit" className="btn btn-primary">
          Create key
        </button>
      </form>
      {secret ? (
        <Modal
          title={`Key “${secret.name}” created`}
          onClose={() => setSecret(null)}
          actions={
            <>
              <button type="button" className="btn" onClick={copy}>
                {copied ? 'Copied' : 'Copy key'}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => setSecret(null)}>
                Done
              </button>
            </>
          }
        >
          <p>Copy it now: it is shown only this once. Only a fingerprint of it is stored.</p>
          <input data-secret readOnly value={secret.secret} aria-label="New API key" onFocus={(e) => e.currentTarget.select()} className="mono" />
        </Modal>
      ) : null}
    </>
  );
}
